import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import Select from 'react-select';
import { Button, Form } from 'react-bootstrap';
import { faCircleArrowDown, faCircleArrowUp } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircleChevronLeft,
  faCircleChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { Event, SocketContext } from '../../../../socketProvider';

import {
  customSelectStylesOptions,
  inRange,
  MAX_ZOOM,
  seatOptions,
  socketEventNames,
} from '../../helper';
import {
  restoreClonedValues,
  updatePath,
  updatePaths,
  updateRegionType,
} from '../../actions';
import { displayErrorModal, displaySuccessModal } from '../../../../helpers/swal';
import camMap from '../../mapLogic';
import { loadMosaicSession, saveMosaicSession, isAllowedMosaicUrl } from '../../mapGridSession';
import {
  CORNER_ORDER,
  emptyMapGridCorner,
  emptyMapGridEditingState,
  MAP_GRID_TILE_KEYS,
  mapRelativeToOnvif,
  ptzToMosaicPixel,
} from '../../mapGridGeometry';
import CornerPosition from '../CornerControls';
import cornerStyle from '../CornerControls/style.module.scss';

import style from './style.module.scss';

const { ROVER_COMMAND, ROVER_MOVE, ROVER_GET_POSITION } = socketEventNames;

const cornerFromRegion = (regionCorner, grid, reference) => {
  const base = emptyMapGridCorner();
  if (!regionCorner) return base;

  const {
    pan, tilt, row, seat,
  } = regionCorner;
  const hasPtz = pan !== '' && pan != null && !Number.isNaN(Number(pan))
    && tilt !== '' && tilt != null && !Number.isNaN(Number(tilt));

  let mosaicX = null;
  let mosaicY = null;
  if (hasPtz && grid) {
    const onvif = mapRelativeToOnvif(pan, tilt, reference);
    const hit = ptzToMosaicPixel(grid, onvif.pan, onvif.tilt);
    if (hit) {
      mosaicX = hit.mosaicX;
      mosaicY = hit.mosaicY;
    }
  }

  return {
    pan: hasPtz ? pan : '',
    tilt: hasPtz ? tilt : '',
    row: row ?? '',
    seat: seat ?? '',
    mosaicX,
    mosaicY,
    ...MAP_GRID_TILE_KEYS.reduce((acc, key) => {
      acc[key] = regionCorner[key] ?? null;
      return acc;
    }, {}),
  };
};

const absoluteCornerPtz = (corner) => {
  const ref = camMap.getReference() || { pan: 0, tilt: 0 };
  return {
    pan: Number(corner.pan) + Number(ref.pan || 0),
    tilt: Number(corner.tilt) + Number(ref.tilt || 0),
  };
};

const ScanBound = ({ name, path }) => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const pan = useSelector(({ mapping }) => get(mapping, `${path}.pan`));
  const tilt = useSelector(({ mapping }) => get(mapping, `${path}.tilt`));
  const zoom = useSelector(({ mapping: { preview: { zoom: { value } } } }) => value);
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const corner = path.split('.')[1];

  const requestGetPosition = () => {
    if (!connectedCamera) return;
    dispatch(updatePath('positionPath', corner));
    socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#');
  };

  const sendPositionToCamera = () => {
    if (!connectedCamera) return;
    const ref = camMap.getReference() || { pan: 0, tilt: 0 };
    const safePan = Number(pan) + Number(ref.pan || 0);
    const safeTilt = Number(tilt) + Number(ref.tilt || 0);
    const safeZoom = Number(zoom / 1000);

    if (Number.isInteger(safePan) && Number.isInteger(safeTilt) && inRange(safeZoom, 0, MAX_ZOOM)) {
      socket.emit(ROVER_MOVE, 'host', connectedCamera, `!${safePan},${safeTilt},${safeZoom}`);
      setTimeout(() => socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#'), 1500);
    }
  };

  return (
    <div className={cornerStyle.cornerBlock}>
      <div>
        <span>{name}</span>
        <div>
          <button
            className={cornerStyle.circleButton}
            onClick={requestGetPosition}
            type="button"
            title="Get position from camera"
            disabled={!connectedCamera}
          >
            <FontAwesomeIcon icon={faCircleArrowDown} />
          </button>
          <button
            className={cornerStyle.circleButton}
            onClick={sendPositionToCamera}
            type="button"
            title="Send position to camera"
            disabled={!connectedCamera}
          >
            <FontAwesomeIcon icon={faCircleArrowUp} />
          </button>
        </div>
      </div>
      <div>
        <div>
          <span>Pan</span>
          <input
            type="number"
            value={pan ?? ''}
            onChange={({ target: { value } }) => dispatch(updatePath(`${path}.pan`, value))}
          />
        </div>
        <div>
          <span>Tilt</span>
          <input
            type="number"
            value={tilt ?? ''}
            onChange={({ target: { value } }) => dispatch(updatePath(`${path}.tilt`, value))}
          />
        </div>
      </div>
    </div>
  );
};


const MapGridTab = () => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const update = useCallback((...args) => dispatch(updatePath(...args)), [dispatch]);
  const createWatchRef = useRef({ token: 0, gotDone: false, poll: null });

  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const zoom = useSelector(({ mapping: { preview: { zoom: { value } } } }) => value);
  const sectionName = useSelector(({ mapping: { mapGrid } }) => mapGrid.sectionName);
  const type = useSelector(({ mapping: { mapGrid } }) => mapGrid.type);
  const regionType = useSelector(({ mapping: { mapGrid } }) => mapGrid.regionType);
  const activeCorner = useSelector(({ mapping: { mapGrid } }) => mapGrid.activeCorner);
  const mosaicUrl = useSelector(({ mapping: { mapGrid } }) => mapGrid.mosaicUrl);
  const lastMosaicUrl = useSelector(({ mapping: { mapGrid } }) => mapGrid.lastMosaicUrl);
  const lastMosaicWidth = useSelector(({ mapping: { mapGrid } }) => mapGrid.lastMosaicWidth);
  const lastMosaicHeight = useSelector(({ mapping: { mapGrid } }) => mapGrid.lastMosaicHeight);
  const creating = useSelector(({ mapping: { mapGrid } }) => mapGrid.creating);
  const createProgress = useSelector(({ mapping: { mapGrid } }) => mapGrid.createProgress);
  const scanStart = useSelector(({ mapping: { mapGrid } }) => mapGrid.scanStart);
  const scanEnd = useSelector(({ mapping: { mapGrid } }) => mapGrid.scanEnd);
  const regionIndex = useSelector(({ mapping: { mapGrid } }) => mapGrid.regionIndex);
  const isSelectDisabled = useSelector(({ mapping: { mapGrid: { disableSelect: v } } }) => v);
  const loadingStatus = useSelector(({ mapping: { loadingStatus: status } }) => status);
  const grid = useSelector(({ mapping: { mapGrid } }) => mapGrid.grid);

  const regionList = useMemo(
    () => camMap.populateMapGridDropDownList(),
    [loadingStatus],
  );

  const selectedListIndex = useMemo(() => {
    if (regionIndex == null) {
      return regionList.findIndex((opt) => opt.isEmptySection
        && opt.sectionName === sectionName
        && String(opt.sectionType).toLowerCase() === String(type).toLowerCase());
    }
    return regionList.findIndex((opt) => opt.index === regionIndex);
  }, [regionList, regionIndex, sectionName, type]);

  const mosaicCameraRef = useRef(connectedCamera);

  useEffect(() => {
    const stored = connectedCamera ? loadMosaicSession(connectedCamera) : null;
    const cameraChanged = mosaicCameraRef.current !== connectedCamera;
    mosaicCameraRef.current = connectedCamera;

    const next = {
      'mapGrid.lastMosaicUrl': stored?.mosaicUrl || null,
      'mapGrid.lastMosaicWidth': stored?.mosaicWidth || null,
      'mapGrid.lastMosaicHeight': stored?.mosaicHeight || null,
      'mapGrid.grid': stored?.grid || null,
    };

    if (cameraChanged) {
      Object.assign(next, emptyMapGridEditingState(), {
        'mapGrid.mosaicUrl': null,
        'mapGrid.mosaicWidth': null,
        'mapGrid.mosaicHeight': null,
        'mapGrid.scanStart': { pan: '', tilt: '' },
        'mapGrid.scanEnd': { pan: '', tilt: '' },
      });
    }

    dispatch(updatePaths(next));
  }, [connectedCamera, loadingStatus, dispatch]);

  useEffect(() => {
    if (regionIndex == null) return;
    const stillThere = regionList.some((opt) => opt.index === regionIndex);
    if (!stillThere && regionList.length) {
      const lastReal = [...regionList].reverse().find((opt) => opt.index != null);
      dispatch(updatePaths({
        'mapGrid.regionIndex': lastReal ? lastReal.index : null,
      }));
    }
  }, [regionList, regionIndex, dispatch]);

  useEffect(() => {
    const selectedOption = regionIndex != null
      ? regionList.find((opt) => opt.index === regionIndex)
      : null;

    if (selectedOption?.value && selectedOption.index != null) {
      const selectedType = selectedOption.sectionType;
      const selectedName = selectedOption.sectionName;
      const region = camMap.getRegion(regionIndex, selectedName, selectedType);
      if (!region?.length) return;

      const reference = camMap.getReference() || { pan: 0, tilt: 0 };
      const positionKeys = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];
      const corners = CORNER_ORDER.map((label, i) => {
        const raw = region.find((c) => c.position === positionKeys[i]) || region[i];
        return cornerFromRegion(raw, grid, reference);
      });

      const updateObject = {
        'mapGrid.selectedRegion': { index: regionIndex, value: selectedOption.value },
        'mapGrid.type': selectedType.toLowerCase(),
        'mapGrid.sectionName': selectedName.trim(),
        'mapGrid.originalSectionName': selectedName.trim(),
        'mapGrid.originalType': selectedType.toLowerCase(),
        'mapGrid.topLeft': corners[0],
        'mapGrid.topRight': corners[1],
        'mapGrid.bottomLeft': corners[2],
        'mapGrid.bottomRight': corners[3],
        'mapGrid.activeCorner': 'topLeft',
      };
      if (region[4]?.zoom_factor != null) {
        updateObject['preview.zoom.value'] = region[4].zoom_factor * 1000;
      }
      updateObject['mapGrid.clonedValues'] = { ...updateObject };
      dispatch(updatePaths(updateObject));
    }
  }, [regionIndex, regionList, grid, dispatch]);

  const handleRoverCommand = useCallback((...args) => {
    const arg = args.find((a) => a && typeof a === 'object' && (
      a.gridCreateProgress || a.gridCreateError || a.gridCreateDone
    ));
    const camera = args.find((a) => typeof a === 'string' && /^cam\d+/i.test(a));
    // Match Browse camera binding; only accept events for an in-flight create
    // (creating flag and/or sync watch token before Redux re-renders).
    const awaitingCreate = creating
      || (Boolean(createWatchRef.current.token) && !createWatchRef.current.gotDone);
    if (!arg || camera !== connectedCamera || !awaitingCreate) return;

    if (arg.gridCreateProgress) {
      if (arg.gridCreateProgress.percent != null) {
        update('mapGrid.createProgress', arg.gridCreateProgress);
      }
      return;
    }

    if (arg.gridCreateError) {
      createWatchRef.current.gotDone = true;
      if (createWatchRef.current.poll) {
        window.clearInterval(createWatchRef.current.poll);
        createWatchRef.current.poll = null;
      }
      dispatch(updatePaths({
        'mapGrid.creating': false,
        'mapGrid.createProgress': null,
      }));
      displayErrorModal({ text: arg.gridCreateError.message || 'grid_create failed' });
      return;
    }

    if (arg.gridCreateDone) {
      createWatchRef.current.gotDone = true;
      if (createWatchRef.current.poll) {
        window.clearInterval(createWatchRef.current.poll);
        createWatchRef.current.poll = null;
      }
      const done = arg.gridCreateDone;
      const mosaicUrlFromDone = done.mosaicUrl || null;
      if (!isAllowedMosaicUrl(mosaicUrlFromDone)) {
        dispatch(updatePaths({
          'mapGrid.creating': false,
          'mapGrid.createProgress': null,
        }));
        displayErrorModal({
          text: 'Mosaic URL from the camera was rejected (expected an HTTPS S3 URL).',
        });
        return;
      }
      const nextGrid = done.gridJson || null;
      const size = nextGrid?.mosaic_size || {};
      const session = {
        mosaicUrl: mosaicUrlFromDone,
        mosaicWidth: size.width || null,
        mosaicHeight: size.height || null,
        grid: nextGrid,
      };

      dispatch(updatePaths({
        'mapGrid.creating': false,
        'mapGrid.createProgress': null,
        'mapGrid.mosaicUrl': session.mosaicUrl,
        'mapGrid.mosaicWidth': session.mosaicWidth,
        'mapGrid.mosaicHeight': session.mosaicHeight,
        'mapGrid.grid': nextGrid,
        'mapGrid.lastMosaicUrl': session.mosaicUrl,
        'mapGrid.lastMosaicWidth': session.mosaicWidth,
        'mapGrid.lastMosaicHeight': session.mosaicHeight,
        ...emptyMapGridEditingState(),
      }));

      saveMosaicSession(session).then((ok) => {
        if (!ok) {
          displayErrorModal({
            text: 'Mosaic is ready locally, but saving it to the camera map file failed. '
              + 'Other machines will not see View Mosaic until save succeeds.',
          });
          return;
        }
        displaySuccessModal({
          text: `Mosaic ready (${done.cols}×${done.rows}). Pick a section or map a new one.`,
        });
      });
    }
  }, [connectedCamera, creating, dispatch, update]);

  const createMosaic = () => {
    if (!connectedCamera) {
      displayErrorModal({ text: 'Connect a camera first.' });
      return;
    }
    if (scanStart.pan === '' || scanStart.tilt === '' || scanEnd.pan === '' || scanEnd.tilt === '') {
      displayErrorModal({ text: 'Set Scan start and Scan end (↓ from live) before creating a mosaic.' });
      return;
    }
    const start = absoluteCornerPtz(scanStart);
    const end = absoluteCornerPtz(scanEnd);
    const zoomOnvif = Number(zoom) / 1000;

    // Clear the open mosaic in Redux for this scan; the cam-file copy is
    // replaced only after a successful grid_create so a failed scan keeps
    // the previous shared mosaic for other machines.
    dispatch(updatePaths({
      'mapGrid.creating': true,
      'mapGrid.createProgress': { percent: 0 },
      'mapGrid.mosaicUrl': null,
    }));

    const token = Date.now();
    createWatchRef.current = { token, gotDone: false, poll: null };

    socket.emit(ROVER_COMMAND, 'host', connectedCamera, {
      command: 'grid_create',
      camera: connectedCamera,
      startPan: start.pan,
      startTilt: start.tilt,
      endPan: end.pan,
      endTilt: end.tilt,
      zoom: zoomOnvif,
    });

    createWatchRef.current.poll = window.setInterval(() => {
      if (createWatchRef.current.token !== token || createWatchRef.current.gotDone) {
        window.clearInterval(createWatchRef.current.poll);
        createWatchRef.current.poll = null;
        return;
      }
      socket.emit(ROVER_COMMAND, 'host', connectedCamera, {
        command: 'grid_session',
        camera: connectedCamera,
      });
    }, 8000);
  };

  const startNewRegion = () => {
    dispatch(updatePaths(emptyMapGridEditingState()));
  };

  const onRegionSelect = (option) => {
    if (!option) {
      startNewRegion();
      return;
    }
    if (option.isEmptySection) {
      dispatch(updatePaths({
        ...emptyMapGridEditingState(),
        'mapGrid.selectedRegion': { index: null, value: option.value },
        'mapGrid.regionIndex': null,
        'mapGrid.sectionName': option.sectionName.trim(),
        'mapGrid.type': String(option.sectionType).toLowerCase(),
        'mapGrid.originalSectionName': null,
        'mapGrid.originalType': null,
        'mapGrid.clonedValues': {},
        'mapGrid.disableSelect': false,
      }));
      return;
    }
    update('mapGrid.regionIndex', option.index);
  };

  const identifying = Boolean(mosaicUrl);

  const regionTypeChangeHandler = (e) => {
    dispatch(updateRegionType(e.target.id.replace(/^mapGrid-/, '')));
  };

  const renderCorner = (label, title) => (
    <div
      className={`${style.mapGridCorner} ${identifying && activeCorner === label ? style.mapGridCornerActive : ''}`}
      onClick={identifying ? () => update('mapGrid.activeCorner', label) : undefined}
      onKeyDown={() => {}}
      role="presentation"
    >
      <CornerPosition name={title} path={`mapGrid.${label}`} hidePtzButtons={identifying} />
    </div>
  );

  let progressLabel = null;
  if (createProgress) {
    const cell = createProgress.cols != null
      ? ` (${createProgress.ix},${createProgress.iy} / ${createProgress.cols}×${createProgress.rows})`
      : '';
    progressLabel = `Scanning ${createProgress.percent ?? 0}%${cell}`;
  }

  const cameraReady = Boolean(connectedCamera);
  const realRegionOptions = regionList.filter((opt) => opt.index != null);
  const canStepPrev = cameraReady && selectedListIndex > 0 && !isSelectDisabled;
  const canStepNext = cameraReady
    && selectedListIndex >= 0
    && selectedListIndex < regionList.length - 1
    && !isSelectDisabled;

  let hintText = 'Set Scan start and Scan end from live (↓), then Create Mosaic.';
  if (identifying) {
    hintText = realRegionOptions.length
      ? 'Pick an existing section to remap, or New for a fresh region. Click a corner card to choose which corner to set, then click the mosaic. Save in the header.'
      : 'Click a corner card to choose which corner to set, then click the mosaic. Save in the header. Close the mosaic for live view; View Mosaic returns to it.';
  }

  return (
    <div className={style.mapGridTab}>
      <Event event={ROVER_COMMAND} handler={handleRoverCommand} />

      {!identifying && (
        <div className={style.mapGridActions}>
          <Button
            variant="outline-primary"
            className="small"
            disabled={creating || !cameraReady}
            onClick={createMosaic}
          >
            {creating ? (progressLabel || 'Creating…') : 'Create Mosaic'}
          </Button>
          {Boolean(lastMosaicUrl) && isAllowedMosaicUrl(lastMosaicUrl) && (
            <Button
              variant="outline-primary"
              className="small"
              disabled={creating || !cameraReady}
              onClick={() => dispatch(updatePaths({
                'mapGrid.mosaicUrl': lastMosaicUrl,
                'mapGrid.mosaicWidth': lastMosaicWidth,
                'mapGrid.mosaicHeight': lastMosaicHeight,
              }))}
            >
              View Mosaic
            </Button>
          )}
        </div>
      )}

      {identifying ? (
        <>
          <div className={style.listSelectorContainer}>
            <button
              type="button"
              disabled={!canStepPrev}
              onClick={() => {
                const prev = regionList[selectedListIndex - 1];
                if (prev) onRegionSelect(prev);
              }}
            >
              <FontAwesomeIcon icon={faCircleChevronLeft} />
            </button>
            <Select
              placeholder="Existing section / region"
              options={regionList}
              isDisabled={isSelectDisabled}
              value={selectedListIndex >= 0 ? regionList[selectedListIndex] : null}
              styles={customSelectStylesOptions}
              onChange={onRegionSelect}
              isClearable
            />
            <button
              type="button"
              disabled={!canStepNext}
              onClick={() => {
                const next = regionList[selectedListIndex + 1];
                if (next) onRegionSelect(next);
              }}
            >
              <FontAwesomeIcon icon={faCircleChevronRight} />
            </button>
          </div>

          <div className={style.mapGridActions}>
            <Button
              variant="outline-primary"
              className="small"
              disabled={!cameraReady}
              onClick={startNewRegion}
            >
              New
            </Button>
            <Button
              variant="outline-primary"
              className="small"
              disabled={!cameraReady || !isSelectDisabled}
              onClick={() => dispatch(restoreClonedValues())}
            >
              Discard
            </Button>
          </div>

          <div className={style.selectRow}>
            <span>Type</span>
            <Select
              placeholder="Section"
              value={seatOptions.find(({ value }) => value === type)}
              options={seatOptions}
              styles={customSelectStylesOptions}
              onChange={({ value }) => update('mapGrid.type', value)}
            />
          </div>
          <div className={style.selectRow}>
            <span>Name</span>
            <input
              type="text"
              className={style.input}
              value={sectionName ?? ''}
              onChange={({ currentTarget: { value } }) => update('mapGrid.sectionName', value)}
              placeholder="Section name"
            />
          </div>
          <div className={style.regionTypeRadioButtons}>
            <Form.Check
              inline
              custom
              type="radio"
              id="mapGrid-default"
              label="Default"
              name="mapGridRegionType"
              checked={regionType === 'default'}
              onChange={regionTypeChangeHandler}
            />
            <Form.Check
              inline
              custom
              type="radio"
              id="mapGrid-singleRow"
              label="Single Row"
              name="mapGridRegionType"
              checked={regionType === 'singleRow'}
              onChange={regionTypeChangeHandler}
            />
            <Form.Check
              inline
              custom
              type="radio"
              id="mapGrid-singleSeat"
              label="Single Seat"
              name="mapGridRegionType"
              checked={regionType === 'singleSeat'}
              onChange={regionTypeChangeHandler}
            />
          </div>
          <div className={style.cornersWrapper}>
            {renderCorner('topLeft', 'Top Left')}
            {renderCorner('topRight', 'Top Right')}
          </div>
          <div className={style.cornersWrapper}>
            {renderCorner('bottomLeft', 'Bottom Left')}
            {renderCorner('bottomRight', 'Bottom Right')}
          </div>
        </>
      ) : (
        <div className={style.cornersWrapper}>
          <ScanBound name="Scan start" path="mapGrid.scanStart" />
          <ScanBound name="Scan end" path="mapGrid.scanEnd" />
        </div>
      )}

      <p className={style.mapGridHint}>
        {hintText}
      </p>
    </div>
  );
};

export default MapGridTab;
